import React, { useEffect, useState, useRef } from "react";
import { Link } from 'react-router-dom';
import { withTranslation } from "react-i18next";
import ReactGA from "react-ga";
import cookies from "js-cookie";
import BlogCard from './BlogCard';
import { Breadcrumb, Title } from "../../../css/commun";
import { URL_Back, URL_Dev } from '../Variables';
import { fetchWpCached, resolvePostImageUrl } from '../../../api/wpClient';
import defaultImg from "../../../photos/blog/nophoto2.png";
import Pages from "../../../Model/Pages";
import './BlogPage.css';
import SeoHead from "../../Seo/SeoHead";

/** Évite un re-render quand le cache local et la revalidation WP renvoient la même liste. */
function postsListSignature(items) {
  if (!Array.isArray(items)) return "";
  return items.map((p) => `${p?.id ?? ""}:${p?.modified ?? p?.date ?? ""}`).join("\u0001");
}

function mergePostsBySignature(prev, next) {
  if (!Array.isArray(next)) return prev;
  if (postsListSignature(prev) === postsListSignature(next)) return prev;
  return next;
}

function pagesListSignature(items) {
  if (!Array.isArray(items)) return "";
  return items.map((p) => `${p?.id ?? ""}:${p?.modified ?? p?.date ?? ""}`).join("\u0001");
}

function mergePagesBySignature(prev, next) {
  if (!Array.isArray(next)) return prev;
  if (pagesListSignature(prev) === pagesListSignature(next)) return prev;
  return next;
}

const BlogPage = (props) => {
  const pathname = props.location.pathname;
  const splitState = pathname.split('/');
  const routeLanguage = props.match?.params?.lng;
  const activeLanguage = ['fr', 'en', 'ar'].includes(routeLanguage)
    ? routeLanguage
    : (cookies.get('i18next') || 'fr');
  const homePath = `/${activeLanguage}`;
  const blogBasePath = `${homePath}/blog`;
  const staticTitles = {
    en: "Dernières actualités et blogs",
    fr: "Dernières actualités et blogs",
    ar: "Dernières actualités et blogs",
  };

  const { t } = props;
  const [pages, setPages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [titre, setTitre] = useState(staticTitles[activeLanguage] || staticTitles.fr);
  const [lastChecked, setLastChecked] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState();
  const lastPagesSigRef = useRef("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchAllPosts = async (query = "") => {
    try {
      setLoading(true);

      if (!query) {
        const allPosts = await fetchWpCached("posts", {
          onRevalidated: (fresh) => {
            setPosts((prev) => mergePostsBySignature(prev, fresh));
          },
        });
        setPosts((prev) => mergePostsBySignature(prev, allPosts));
        return;
      }

      const allPosts = [];
      let page = 1;
      const perPage = 100;

      while (true) {
        const response = await fetch(
          `${URL_Back}/wp-json/wp/v2/posts?per_page=${perPage}&page=${page}${query}`
        );
        const result = await response.json();
        if (!Array.isArray(result) || result.length === 0) break;
        allPosts.push(...result);
        if (result.length < perPage) break;
        page += 1;
      }

      setPosts(allPosts);
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      await fetchAllPosts(`&search=${encodeURIComponent(searchQuery)}`);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  useEffect(() => {
    const fetchPostsBySearch = async () => {
      if (!searchQuery.trim()) {
        fetchAllPosts();
        return;
      }

      try {
        await fetchAllPosts(`&search=${encodeURIComponent(searchQuery)}`);
      } catch (error) {
        console.error("Error fetching search results:", error);
      }
    };

    fetchPostsBySearch();
  }, [searchQuery]);

  const applyPagesAndMeta = (result) => {
    const sig = pagesListSignature(result);
    if (sig && sig === lastPagesSigRef.current) {
      return;
    }
    if (sig) {
      lastPagesSigRef.current = sig;
    }

    setPages((prev) => mergePagesBySignature(prev, result));

    const matchedPageForTitle = result.find((element) =>
      element?.slug &&
      decodeURI(element.slug) === (splitState[1].length === 2 ? splitState[2] : splitState[1])
    );

    if (!matchedPageForTitle) {
      return;
    }

    const raw = matchedPageForTitle?.title?.rendered || "";
    const nextTitle = raw
      .replace(/&#8217;/g, "'")
      .replace(/<[^>]*>/g, "")
      .trim();
    setTitre((prev) => {
      if (!nextTitle) return prev;
      const prevNorm = String(prev || "").replace(/<[^>]*>/g, "").trim();
      return nextTitle === prevNorm ? prev : nextTitle;
    });
    setLastChecked(new Date().getTime());
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    ReactGA.pageview(window.location.pathname);

    const oneMonth = 30 * 24 * 60 * 60 * 1000;
    const lastCheckedTimestamp = lastChecked || 0;

    if (new Date().getTime() - lastCheckedTimestamp >= oneMonth) {
      fetchWpCached("pages", {
        onRevalidated: (result) => applyPagesAndMeta(result),
      }).then((result) => applyPagesAndMeta(result));
    }
  }, [lastChecked]);

  const matchedPage = pages.find((element) => {
    if (!element?.slug) {
      return false;
    }

    return decodeURI(element.slug) === (splitState[1].length === 2 ? splitState[2] : splitState[1]);
  });

  const page = matchedPage
    ? new Pages(
        matchedPage?.id,
        matchedPage?.title?.rendered || "",
        matchedPage?.content?.rendered || "",
        matchedPage?.parent,
        matchedPage?.slug,
        matchedPage?.yoast_head_json?.og_description || "",
        matchedPage?.yoast_head_json?.title || "",
        matchedPage?.date,
        matchedPage?.excerpt?.rendered || ""
      )
    : null;

  const blogPageTitle = page?.metatitle || "Oxton Digital | Blog";
  const blogPageDescription =
    page?.description ||
    "Retrouvez les actualites, conseils SEO, tendances web et retours d'experience de l'equipe Oxton Digital.";
  const blogPageImage = `${URL_Dev}/images/oxton.png`;

  return (
    <>
      <SeoHead
        title={blogPageTitle}
        description={blogPageDescription}
        pathname={props.location.pathname}
        image={blogPageImage}
        type="website"
      />
      <div className="blog-page container">
        <Breadcrumb>
          <p>
            <Link className="retour" to={homePath}>{t('Home_link')}</Link> / <span>Blog</span>
          </p>
        </Breadcrumb>
        <Title>
          <h1>{titre}</h1>
          <div className="d-flex search-title-block">
            <p className="title-p">Boostez votre visibilité digitale avec nos conseils d’experts ! Astuces, stratégies et tendances pour attirer plus de visiteurs et convertir vos prospects en clients dès aujourd’hui.</p>
            <div className="search-bar">
              <div className="search-input-container">
                <svg xmlns="http://www.w3.org/2000/svg" onClick={handleSearch} className="search-icon" width="27" height="27" viewBox="0 0 27 27" fill="none">
                  <path d="M26.7444 25.5311L19.7733 18.6715C21.5988 16.6882 22.7204 14.0652 22.7204 11.1789C22.7195 5.00458 17.6338 0 11.3598 0C5.08573 0 0 5.00458 0 11.1789C0 17.3533 5.08573 22.3579 11.3598 22.3579C14.0706 22.3579 16.5569 21.4202 18.5099 19.8615L25.508 26.7481C25.849 27.084 26.4026 27.084 26.7436 26.7481C27.0853 26.4122 27.0853 25.867 26.7444 25.5311ZM11.3598 20.6379C6.05125 20.6379 1.74786 16.403 1.74786 11.1789C1.74786 5.95486 6.05125 1.71994 11.3598 1.71994C16.6683 1.71994 20.9717 5.95486 20.9717 11.1789C20.9717 16.403 16.6683 20.6379 11.3598 20.6379Z" fill="#82878B" />
                </svg>
                <input
                  type="text"
                  placeholder="Recherche..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
          </div>
        </Title>

        <div className="blog-grid mt-5">
          {(posts.length === 0 && loading === false) ? (
            <p className="title-p">Aucun article disponible pour le moment.</p>
          ) : (
            posts.map((post) => {
              const slug = post?.slug;
              const title = post?.title?.rendered;

              if (!slug || !title) {
                return null;
              }

              const parser = new DOMParser();
              const doc = parser.parseFromString(post?.content?.rendered || "", "text/html");
              const dateDiv = doc.querySelector(".date");
              const dateText = dateDiv ? dateDiv.textContent : "";

              return (
                <BlogCard
                  key={post?.id || slug}
                  blog={{
                    id: post?.id,
                    title,
                    date: dateText,
                    description: post?.yoast_head_json?.description || "",
                    imageUrl: resolvePostImageUrl(post, "article_img2_content") || defaultImg,
                    link: {
                      pathname: `${blogBasePath}/${slug}`,
                      state: { postData: posts },
                    },
                  }}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default withTranslation()(BlogPage);
